import { Integration, IntegrationPartner } from "refsix-js-models";

import { assignmentPlatformIds } from "./assignmentPlatforms";

/**
 * How a match relates to the partner it came from, for the screens that have to
 * decide whether the referee may change or delete it.
 *
 * Two independent axes, and keeping them apart is the point:
 *
 *  - `manualSync` — set on the partners with no API (the scraped assignment
 *    platforms), where REFSIX only sees a fixture when the referee runs an
 *    import. Fixed for the lifetime of the match.
 *  - `disconnected` — set by the referee on one match, to stop the next sync
 *    overwriting their edits. Honoured server-side in
 *    `globalIntegrationService._processUploadedRefSixMatches`.
 */

type WithIntegration = { integration?: Integration };

/** Partners that have no API, so their matches only update when asked. */
function manualSyncPartners(): IntegrationPartner[] {
    return assignmentPlatformIds();
}

/**
 * Whether a future sync could overwrite the referee's edits to this match
 * without them asking for it.
 *
 * The delete gates key off this rather than `!!match.integration`: a manual
 * match can be deleted safely, because nothing brings it back until the referee
 * runs the next import themselves.
 */
export function isLiveIntegrationMatch(match: WithIntegration): boolean {
    // Presence of the object, not of `partner`: the delete gates this replaces
    // keyed off the object, and an integration without a partner is not real
    // data — narrowing it here would quietly change their behaviour.
    return !!match.integration && !match.integration.manualSync;
}

export function isManualSyncMatch(match: WithIntegration): boolean {
    return match.integration?.manualSync === true;
}

/**
 * Partners whose matches offer the disconnect/reconnect card in the edit form.
 *
 * The scraped platforms are here because a tournament bracket is the case the
 * feature exists for: Arbiter names the teams in a knockout round before the
 * preceding round has been played, so the fixture arrives as "TBD v TBD" or with
 * last round's winners, and the referee has to be able to put the real teams in
 * without the next import undoing it.
 */
export function canDisconnectIntegrationMatch(
    partner: IntegrationPartner | undefined
): boolean {
    if (!partner) {
        return false;
    }
    return [
        IntegrationPartner.assignr,
        IntegrationPartner.refinsight,
        IntegrationPartner.refassist,
        IntegrationPartner.soccerRefManager,
        ...manualSyncPartners(),
    ].includes(partner);
}

/**
 * Whether the edit form locks the fields a sync overwrites — competition, venue,
 * team names and kick-off.
 *
 * refauth's `_copyMatchDetails` preserves none of those for any partner, so in
 * principle every connected integration match should lock them. Only the
 * partners listed here do today; widening it is a behaviour change for those
 * users rather than a tidy-up, so it stays a list.
 *
 * Callers must combine this with the *form's* disconnected value, not the saved
 * match's, so the fields unlock the moment the referee taps Disconnect.
 */
export function integrationLocksSyncedFields(
    partner: IntegrationPartner | undefined
): boolean {
    if (!partner) {
        return false;
    }
    return [IntegrationPartner.assignr, ...manualSyncPartners()].includes(
        partner
    );
}
